import { type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { Command } from '../types/command';

import {
  type ClearSource,
  CommandRegistrationContext,
  type CommandRegistrationContextValue,
  CommandRegistryContext,
  type CommandRegistryContextValue,
  type SetCommandsForSource,
} from './command-registry-context';

export interface CommandRegistryProviderProps {
  children: ReactNode;
}

/**
 * Registry state.
 *
 * `commands` is a plain object keyed by id. Property-iteration order
 * is JS-spec'd as insertion order; **overwriting an existing key
 * does NOT change its position**. That's the load-bearing detail.
 *
 * `bySource` tracks which ids each `useCommands` call site owns, so
 * a dep-change re-run of that hook can be reconciled by id (update
 * existing, append new, remove gone) instead of wholesale-replaced
 * (delete + reinsert at end, which would drift section positions).
 */
interface RegistryState {
  commands: Record<string, Command>;
  bySource: Map<symbol, Set<string>>;
}

const EMPTY_STATE: RegistryState = { commands: {}, bySource: new Map() };

/**
 * Compute the next state after a source re-publishes its commands.
 * Pure; throws on invariant violations so the throw propagates up
 * through React's render pipeline and is caught by the outer
 * ErrorBoundary in CommandPaletteProvider.
 */
function reduceSetCommands(prev: RegistryState, sourceKey: symbol, nextCommands: Command[]): RegistryState {
  // Validate: no id collisions with other sources. (A source can,
  // and routinely will, re-publish its OWN ids -- that's the whole
  // point of the patch-in-place behaviour.)
  for (const cmd of nextCommands) {
    for (const [otherKey, otherIds] of prev.bySource) {
      if (otherKey !== sourceKey && otherIds.has(cmd.id)) {
        throw new Error(
          `[command-palette] duplicate command id "${cmd.id}". ` +
            `IDs must be unique across the entire registry. ` +
            `Two different useCommands callers are trying to register the same id.`,
        );
      }
    }
  }

  const prevIds = prev.bySource.get(sourceKey) ?? new Set<string>();
  const nextIds = new Set<string>(nextCommands.map((c) => c.id));

  const commands: Record<string, Command> = { ...prev.commands };

  // Drop ids this source previously owned but no longer publishes.
  // Iterating prevIds (not the whole record) means we only touch
  // entries owned by *this* source -- others stay put.
  for (const id of prevIds) {
    if (!nextIds.has(id)) {
      delete commands[id];
    }
  }

  // Upsert each id in publication order. Existing ids overwrite in
  // place; new ids append at the end of property-iteration order.
  for (const cmd of nextCommands) {
    commands[cmd.id] = cmd;
  }

  // Validate: every declared parent exists. Run against the candidate
  // `commands` so siblings registered in the same publication satisfy
  // each other's parent references.
  for (const cmd of nextCommands) {
    if (cmd.parent !== undefined && commands[cmd.parent] === undefined) {
      throw new Error(
        `[command-palette] command "${cmd.id}" declares parent "${cmd.parent}" ` +
          `but no such command is registered. Register the parent first, or in ` +
          `the same publication as the child.`,
      );
    }
  }

  const bySource = new Map(prev.bySource);
  if (nextIds.size === 0) {
    bySource.delete(sourceKey);
  } else {
    bySource.set(sourceKey, nextIds);
  }

  return { commands, bySource };
}

function reduceClearSource(prev: RegistryState, sourceKey: symbol): RegistryState {
  const ownedIds = prev.bySource.get(sourceKey);
  if (ownedIds === undefined) {
    return prev;
  }
  const commands: Record<string, Command> = { ...prev.commands };
  for (const id of ownedIds) {
    delete commands[id];
  }
  const bySource = new Map(prev.bySource);
  bySource.delete(sourceKey);
  return { commands, bySource };
}

/**
 * Provider for command registry state.
 *
 * The registry is reconciled per-source. Each `useCommands` call owns
 * a stable `sourceKey`; when its deps change, it re-publishes its
 * current command set via `setCommandsForSource`. The registry diffs
 * the new set against what that source previously owned and updates
 * the underlying flat `Record<id, Command>` accordingly.
 *
 * Two invariants throw at registration time (both are programmer
 * bugs, not user-visible conditions):
 *
 *   - ID collision: two different useCommands callers tried to claim
 *     the same id.
 *   - Orphan parent: a command's `parent` field references an id
 *     that doesn't exist anywhere in the registry after the
 *     publication.
 *
 * A throw inside the state updater propagates up through React's
 * render pipeline and is caught by the outer `<ErrorBoundary>` in
 * `CommandPaletteProvider`, falling back to the Disabled* providers.
 */
export function CommandRegistryProvider({ children }: CommandRegistryProviderProps) {
  const [state, setState] = useState<RegistryState>(EMPTY_STATE);

  const setCommandsForSource = useCallback<SetCommandsForSource>((sourceKey, nextCommands) => {
    setState((prev) => reduceSetCommands(prev, sourceKey, nextCommands));
  }, []);

  const clearSource = useCallback<ClearSource>((sourceKey) => {
    setState((prev) => reduceClearSource(prev, sourceKey));
  }, []);

  const registrationValue = useMemo<CommandRegistrationContextValue>(
    () => ({ setCommandsForSource, clearSource }),
    [setCommandsForSource, clearSource],
  );
  const registryValue = useMemo<CommandRegistryContextValue>(() => ({ commands: state.commands }), [state.commands]);

  return (
    <CommandRegistrationContext.Provider value={registrationValue}>
      <CommandRegistryContext.Provider value={registryValue}>{children}</CommandRegistryContext.Provider>
    </CommandRegistrationContext.Provider>
  );
}

/**
 * Hook to access the full registry (read surface). Re-renders the
 * caller on every register/unregister. Reserved for palette
 * internals -- consumers that only need to register commands should
 * use `useCommands` (which subscribes only to `CommandRegistrationContext`).
 */
export function useCommandRegistry(): CommandRegistryContextValue {
  const ctx = useContext(CommandRegistryContext);
  if (ctx === null) {
    throw new Error(
      'useCommandRegistry must be used within a <CommandRegistryProvider>. ' +
        'Wrap your tree with <CommandPaletteProvider>.',
    );
  }
  return ctx;
}

/**
 * Hook to access the registration primitives only. Stable identities;
 * subscribers never re-render on registry churn. Used internally by
 * `useCommands`.
 */
export function useCommandRegistration(): CommandRegistrationContextValue {
  const ctx = useContext(CommandRegistrationContext);
  if (ctx === null) {
    throw new Error(
      'useCommandRegistration must be used within a <CommandRegistryProvider>. ' +
        'Wrap your tree with <CommandPaletteProvider>.',
    );
  }
  return ctx;
}
